import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ChevronsRight, MessageSquare } from 'lucide-react'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { formatDurationMinutes as fmt } from '@/lib/formatDuration'
import { postRecoveryApi, type PostRecoveryTask } from './api'
import { formatDateTime } from '@/lib/dateUtils'

/**
 * What the runbook said against what happened, task by task, for one run.
 *
 * THE SPARSE CASE IS THE MAIN CASE. Across the whole estate there are three usable task
 * measurements; a typical run has two measured tasks out of fifteen and the rest were ticked
 * straight to Completed. So "nothing measured" is not a fallback branch bolted on at the end — it
 * is what this chart mostly draws, and it has to look deliberate. Every figure above the chart
 * carries the number of tasks it rests on, and the plan bars still render, because the plan is
 * real data even when the actuals are not.
 *
 * NOTHING IS DISCARDED TO MAKE THE PICTURE TIDY. An abandoned timer — seven weeks against a
 * five-minute plan — is drawn, clipped, in its own colour, and named as an abandoned timer. It is
 * kept out of the headline sums but never out of the chart: a silently dropped row is precisely
 * how a sum becomes a fiction that nobody can trace back.
 *
 * Everything here is ELAPSED time, so formatEffortMinutes has no business on this screen — there
 * is no person-time on the axis and no total that several people could work through at once.
 */

const ROW_H = 48
// Actual on top, plan beneath — the same split the recovery Gantt uses, so a reader who knows one
// chart is not relearning the other. The plan is the reference line, not the headline.
const ACTUAL_TOP = 10
const ACTUAL_H = 12
const PLAN_TOP = 28
const PLAN_H = 8

const GUTTER = 'w-[272px]'

/** Round intervals a person reads without doing arithmetic: minutes, quarter-hours, hours, days. */
const TICK_STEPS = [1, 2, 5, 10, 15, 20, 30, 60, 90, 120, 180, 240, 360, 480, 720, 1440, 2880, 10080]

/**
 * Below this, a plan-versus-actual comparison for the run is still worth showing but must not be
 * read as a pattern. It is the same idea as ObjectHistory's coverage.minObservations, which the
 * server owns for cross-run averages; this screen compares one run, so the threshold lives here.
 */
const MIN_MEANINGFUL_TASKS = 3

type Tone = 'onPlan' | 'over' | 'wellOver' | 'noPlan' | 'outlier'
type RowState = 'measured' | 'unmeasured' | 'notStarted'
type View = 'all' | 'measured' | 'unmeasured'

const ACTUAL_TONE: Record<Tone, string> = {
  onPlan: 'bg-emerald-500/80 border-emerald-600/70',
  over: 'bg-amber-500/85 border-amber-600/70',
  wellOver: 'bg-rose-500/85 border-rose-600/70',
  // No planned duration means there is no variance to colour by. Painting it emerald would claim
  // the task came in on a plan that was never written down.
  noPlan: 'bg-slate-400/70 border-slate-500/60',
  outlier: 'bg-violet-500/80 border-violet-600/70',
}

/** Waiting is not work, so it is not a solid bar. Slate hatching reads in either theme. */
const HATCH = {
  backgroundImage:
    'repeating-linear-gradient(45deg, rgb(148 163 184 / 0.7) 0 3px, rgb(148 163 184 / 0.12) 3px 6px)',
}

const VIEWS: { value: View; label: string }[] = [
  { value: 'all', label: 'All tasks' },
  { value: 'measured', label: 'Measured' },
  { value: 'unmeasured', label: 'Unmeasured' },
]

function rowStateOf(task: PostRecoveryTask): RowState {
  if (task.actualStartMin == null) return 'notStarted'
  if (!task.durationMeasured || task.actualMinutes == null) return 'unmeasured'
  return 'measured'
}

function toneOf(task: PostRecoveryTask): Tone {
  if (task.isOutlier) return 'outlier'
  const planned = task.plannedMinutes
  const actual = task.actualMinutes
  if (actual == null || planned == null || planned <= 0) return 'noPlan'
  const variance = task.varianceMinutes ?? actual - planned
  if (variance <= 0) return 'onPlan'
  return actual >= planned * 2 ? 'wellOver' : 'over'
}

interface Segment { left: number; width: number }

interface Geometry {
  plan: Segment | null
  waiting: Segment | null
  actual: Segment | null
  /** Where the tick goes for a task whose duration was never observed. */
  marker: number | null
  /** The bar runs past the right-hand edge of the axis and is drawn cut off. */
  clipped: boolean
}

function buildGeometry(task: PostRecoveryTask, axisMax: number): Geometry {
  const pct = (m: number) => (m / axisMax) * 100
  const clamp = (v: number) => Math.min(Math.max(v, 0), 100)

  // A plan with no resolved start cannot be placed on the axis, and guessing zero for it would put
  // a bar at T+0 that the runbook never asked for.
  const plan = task.plannedStartMin != null && task.plannedMinutes != null
    ? { left: clamp(pct(task.plannedStartMin)), width: Math.min(pct(task.plannedMinutes), 100 - clamp(pct(task.plannedStartMin))) }
    : null

  const state = rowStateOf(task)
  if (state === 'notStarted') return { plan, waiting: null, actual: null, marker: null, clipped: false }

  const startMin = task.actualStartMin as number
  const start = clamp(pct(startMin))

  const waiting = task.waitingMinutes != null && task.waitingMinutes > 0
    ? (() => {
        const from = clamp(pct(Math.max(startMin - task.waitingMinutes!, 0)))
        return { left: from, width: Math.max(start - from, 0) }
      })()
    : null

  if (state === 'unmeasured') return { plan, waiting, actual: null, marker: start, clipped: false }

  const rawWidth = pct(task.actualMinutes as number)
  const room = 100 - start
  return {
    plan,
    waiting,
    actual: { left: start, width: Math.min(rawWidth, room) },
    marker: null,
    clipped: rawWidth > room + 0.01,
  }
}

function Pill({ tone, children }: { tone: 'muted' | 'violet'; children: React.ReactNode }) {
  return (
    <span className={cn(
      'inline-flex w-fit items-center rounded px-1.5 py-px text-[10px] leading-tight',
      tone === 'violet'
        ? 'bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-300'
        : 'bg-muted text-muted-foreground',
    )}>
      {children}
    </span>
  )
}

function LegendItem({ swatch, label }: { swatch: React.ReactNode; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      {swatch}
      {label}
    </span>
  )
}

function Stat({ label, value, detail, tint }: {
  label: string; value: string; detail: string; tint: string
}) {
  return (
    <div className={cn('rounded-lg border p-3', tint)}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-2xl font-semibold tabular-nums">{value}</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{detail}</div>
    </div>
  )
}

export function PlannedVsActualGantt({ requestId, onOpenComments }: {
  requestId: number
  onOpenComments?: (task: PostRecoveryTask) => void
}) {
  const { t } = useTranslation()
  const [view, setView] = useState<View>('all')

  const query = useQuery({
    queryKey: ['post-recovery-run-tasks', requestId],
    queryFn: () => postRecoveryApi.runTasks(requestId),
  })

  const model = useMemo(() => {
    const tasks = query.data?.tasks ?? []
    const run = query.data?.run ?? null

    const ordered = [...tasks].sort((a, b) =>
      (a.taskNo ?? Number.MAX_SAFE_INTEGER) - (b.taskNo ?? Number.MAX_SAFE_INTEGER)
      || (a.plannedStartMin ?? 0) - (b.plannedStartMin ?? 0)
      || a.id - b.id)

    // THE SCALE IGNORES WHERE AN OUTLIER ENDS. One task left open for seven weeks against a
    // five-minute plan compresses every honest bar on the chart into a hairline; its START is on
    // the axis, its end is not, and the bar itself is drawn cut off at the edge.
    let domain = 0
    const bump = (v: number) => { if (Number.isFinite(v) && v > domain) domain = v }
    for (const task of ordered) {
      const planStart = task.plannedStartMin ?? 0
      bump(task.plannedMinutes != null ? planStart + task.plannedMinutes : planStart)
      if (task.actualStartMin != null) {
        bump(task.actualStartMin)
        if (!task.isOutlier && task.actualMinutes != null) bump(task.actualStartMin + task.actualMinutes)
      }
    }

    const step = TICK_STEPS.find((s) => Math.max(domain, 1) / s <= 7) ?? TICK_STEPS[TICK_STEPS.length - 1]
    const axisMax = Math.max(Math.ceil(Math.max(domain, 1) / step) * step, step)
    const ticks: number[] = []
    for (let m = 0; m <= axisMax; m += step) ticks.push(m)

    const measuredRows = ordered.filter((task) => rowStateOf(task) === 'measured')
    const headline = measuredRows.filter((task) => !task.isOutlier)

    return {
      ordered,
      axisMax,
      ticks,
      // Coverage is the run's own figure where the run row exists, so this header cannot disagree
      // with the set overview that quotes the same columns.
      totalTasks: run?.taskCount ?? ordered.length,
      measuredCount: run?.tasksMeasured ?? measuredRows.length,
      outlierCount: run?.tasksOutlier ?? ordered.filter((task) => task.isOutlier).length,
      notStartedCount: ordered.filter((task) => rowStateOf(task) === 'notStarted').length,
      plannedAll: ordered.reduce((sum, task) => sum + (task.plannedMinutes ?? 0), 0),
      headlineCount: headline.length,
      headlinePlanned: headline.reduce((sum, task) => sum + (task.plannedMinutes ?? 0), 0),
      headlineActual: headline.reduce((sum, task) => sum + (task.actualMinutes ?? 0), 0),
    }
  }, [query.data])

  const { axisMax, ticks, ordered } = model
  const pct = (m: number) => (m / axisMax) * 100

  // Filtering never touches the scale. Switching to "Measured" to look at two rows must not silently
  // rescale the axis under them and make the same bar a different length than it was a click ago.
  const visible = useMemo(() => ordered.filter((task) => {
    if (view === 'all') return true
    const measured = rowStateOf(task) === 'measured'
    return view === 'measured' ? measured : !measured
  }), [ordered, view])

  if (query.isLoading) {
    return (
      <div className="rounded-xl border p-8 text-center text-sm text-muted-foreground">
        {t('common.loading', 'Loading…')}
      </div>
    )
  }

  if (query.isError) {
    return (
      <div className="rounded-xl border border-rose-500/40 bg-rose-500/5 p-4 text-sm">
        {t('postRecovery.pvaLoadFailed', 'The tasks for this run could not be read.')}
      </div>
    )
  }

  if (ordered.length === 0) {
    return (
      <div className="rounded-xl border p-6">
        <p className="text-sm font-medium">{t('postRecovery.pvaNoTasksTitle', 'This run has no tasks')}</p>
        <p className="mt-1 max-w-xl text-xs text-muted-foreground">
          {t('postRecovery.pvaNoTasksBody', 'Tasks appear here once the object has a runbook attached and the recovery request is approved. Until then there is no plan to compare anything against.')}
        </p>
      </div>
    )
  }

  const variance = model.headlineActual - model.headlinePlanned
  const headlineTint = model.headlineCount === 0
    ? 'bg-slate-50 dark:bg-slate-900/40'
    : variance > 0
      ? (model.headlineActual >= model.headlinePlanned * 2
          ? 'bg-rose-50 dark:bg-rose-950/40'
          : 'bg-amber-50 dark:bg-amber-950/40')
      : 'bg-emerald-50 dark:bg-emerald-950/40'

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold">
              {t('postRecovery.pvaTitle', 'Planned against actual')}
            </h3>
            {/* The coverage line is the first thing on the screen, not a footnote. Every bar below
                is read differently once you know how few of them were measured. */}
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
              <span className="tabular-nums">
                {t('postRecovery.pvaCoverage', '{{measured}} of {{total}} tasks measured', {
                  measured: model.measuredCount, total: model.totalTasks,
                })}
              </span>
              {model.outlierCount > 0 && (
                <>
                  <span aria-hidden>·</span>
                  <span className="tabular-nums text-violet-700 dark:text-violet-400">
                    {t('postRecovery.pvaCoverageOutliers', '{{n}} abandoned timer(s), kept out of the figures', { n: model.outlierCount })}
                  </span>
                </>
              )}
              {model.notStartedCount > 0 && (
                <>
                  <span aria-hidden>·</span>
                  <span className="tabular-nums">
                    {t('postRecovery.pvaCoverageNotStarted', '{{n}} never started', { n: model.notStartedCount })}
                  </span>
                </>
              )}
            </p>
          </div>
          <SegmentedControl
            size="sm"
            ariaLabel={t('postRecovery.pvaViewLabel', 'Which tasks to show')}
            value={view}
            onValueChange={setView}
            options={VIEWS.map((v) => ({ value: v.value, label: t(`postRecovery.pvaView_${v.value}`, v.label) }))}
          />
        </div>

        {model.measuredCount === 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500" aria-hidden />
            <p className="text-muted-foreground">
              <span className="font-medium text-amber-700 dark:text-amber-500">
                {t('postRecovery.pvaNoneMeasuredTitle', 'No task on this run has a measured duration')}
              </span>{' '}
              {t('postRecovery.pvaNoneMeasuredBody', 'A duration is recorded when an engineer moves a task to Started and completes it from there. Tasks ticked straight to Completed record no elapsed time, so the bars below are the plan — what was promised, not what it cost.')}
            </p>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          <Stat
            label={t('postRecovery.pvaStatPlanned', 'Planned, whole run')}
            value={fmt(model.plannedAll)}
            detail={t('postRecovery.pvaStatPlannedDetail', 'across {{n}} tasks — the plan is complete even where the actuals are not', { n: model.totalTasks })}
            tint="bg-sky-50 dark:bg-sky-950/40"
          />
          {/* The two figures below cover the SAME tasks, which is the only way a planned-versus-actual
              total means anything when eleven of fifteen tasks were never timed. Summing every plan
              against four actuals would report the run finishing early, every time. */}
          <Stat
            label={t('postRecovery.pvaStatMeasuredPlan', 'Planned, measured tasks')}
            value={model.headlineCount ? fmt(model.headlinePlanned) : '—'}
            detail={t('postRecovery.pvaStatMeasuredPlanDetail', '{{n}} tasks with an observed duration', { n: model.headlineCount })}
            tint="bg-violet-50 dark:bg-violet-950/40"
          />
          <Stat
            label={t('postRecovery.pvaStatMeasuredActual', 'Actual, those same tasks')}
            value={model.headlineCount ? fmt(model.headlineActual) : '—'}
            detail={model.headlineCount === 0
              ? t('postRecovery.pvaStatNoneDetail', 'nothing measured yet, so there is nothing to compare')
              : variance === 0
                ? t('postRecovery.pvaStatOnPlan', 'exactly the plan for those tasks')
                : variance > 0
                  ? t('postRecovery.pvaStatOver', '{{d}} over the plan for those tasks', { d: fmt(variance) })
                  : t('postRecovery.pvaStatUnder', '{{d}} under the plan for those tasks', { d: fmt(-variance) })}
            tint={headlineTint}
          />
        </div>

        {model.headlineCount > 0 && model.headlineCount < MIN_MEANINGFUL_TASKS && (
          <p className="text-[11px] text-amber-700 dark:text-amber-400">
            {t('postRecovery.pvaThinEvidence', 'That comparison rests on {{n}} of {{total}} tasks. It is evidence about those tasks, not yet a pattern for the run.', {
              n: model.headlineCount, total: model.totalTasks,
            })}
          </p>
        )}

        {/* Five bar treatments is well past what anyone infers from the picture alone. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
          <LegendItem
            label={t('postRecovery.pvaLegendPlan', 'Plan')}
            swatch={<i className="h-2 w-5 rounded-sm border border-dashed border-slate-400/80 bg-slate-200/50 dark:border-slate-500/80 dark:bg-slate-700/40" />}
          />
          <LegendItem
            label={t('postRecovery.pvaLegendActual', 'Actual — colour is variance, not status')}
            swatch={
              <span className="flex gap-0.5">
                <i className={cn('h-2.5 w-2.5 rounded-sm border', ACTUAL_TONE.onPlan)} />
                <i className={cn('h-2.5 w-2.5 rounded-sm border', ACTUAL_TONE.over)} />
                <i className={cn('h-2.5 w-2.5 rounded-sm border', ACTUAL_TONE.wellOver)} />
              </span>
            }
          />
          <LegendItem
            label={t('postRecovery.pvaLegendWaiting', 'Waiting on dependencies')}
            swatch={<i className="h-2.5 w-5 rounded-sm border border-slate-400/50" style={HATCH} />}
          />
          <LegendItem
            label={t('postRecovery.pvaLegendUnmeasured', 'Duration not measured')}
            swatch={<i className="h-3.5 w-0.5 rounded-full bg-foreground/70" />}
          />
          <LegendItem
            label={t('postRecovery.pvaLegendOutlier', 'Abandoned timer, drawn cut off')}
            swatch={
              <span className="flex items-center">
                <i className={cn('h-2.5 w-4 rounded-l-sm border', ACTUAL_TONE.outlier)} />
                <ChevronsRight className="h-3 w-3 text-violet-600 dark:text-violet-400" />
              </span>
            }
          />
        </div>

        {/* Wide content scrolls in here, never the page. */}
        <div className="overflow-x-auto rounded-xl border">
          <div className="min-w-[860px]">

            <div className="flex border-b bg-muted/30">
              <div className={cn(GUTTER, 'sticky left-0 z-10 shrink-0 border-r bg-muted/30 px-3 py-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground')}>
                {t('postRecovery.pvaColTask', 'Task')}
              </div>
              <div className="relative h-8 flex-1">
                {ticks.map((m, i) => (
                  <div key={m} className="absolute inset-y-0 border-l border-border/60" style={{ left: `${pct(m)}%` }}>
                    {/* The last tick sits on the right-hand edge, so its label hangs to the LEFT of
                        the line rather than off the end of the chart. */}
                    <span className={cn(
                      'absolute top-2 whitespace-nowrap text-[10px] text-muted-foreground',
                      i === ticks.length - 1 ? 'right-1' : 'left-1',
                    )}>
                      {m === 0 ? t('postRecovery.pvaOrigin', 'T+0') : fmt(m)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {visible.length === 0 ? (
              <div className="p-6">
                <p className="text-sm font-medium">
                  {view === 'measured'
                    ? t('postRecovery.pvaNoMeasuredTitle', 'No measured tasks on this run')
                    : t('postRecovery.pvaNoUnmeasuredTitle', 'Every task on this run was measured')}
                </p>
                <p className="mt-1 max-w-xl text-xs text-muted-foreground">
                  {view === 'measured'
                    ? t('postRecovery.pvaNoMeasuredBody', 'Durations are recorded when an engineer moves a task to Started before completing it. Every task here went straight to Completed or has not been picked up.')
                    : t('postRecovery.pvaNoUnmeasuredBody', 'Every task has both a start and an elapsed time recorded against it — rare enough on this data to be worth noting.')}
                </p>
              </div>
            ) : visible.map((task) => {
              const state = rowStateOf(task)
              const tone = toneOf(task)
              const g = buildGeometry(task, axisMax)
              const interactive = Boolean(onOpenComments)
              const name = task.task || task.taskKey || t('postRecovery.pvaUntitled', 'Untitled task')

              return (
                <div
                  key={task.id}
                  className={cn(
                    'group flex border-b outline-none last:border-b-0',
                    interactive && 'cursor-pointer hover:bg-muted/40 focus-visible:bg-muted/40',
                  )}
                  style={{ height: ROW_H }}
                  role={interactive ? 'button' : undefined}
                  tabIndex={interactive ? 0 : undefined}
                  onClick={interactive ? () => onOpenComments?.(task) : undefined}
                  onKeyDown={interactive ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenComments?.(task) }
                  } : undefined}
                >
                  <div className={cn(GUTTER, 'sticky left-0 z-10 flex shrink-0 flex-col justify-center gap-0.5 border-r bg-background px-3', interactive && 'group-hover:bg-muted/40')}>
                    <div className="flex items-center gap-2">
                      <span className="w-5 shrink-0 text-right text-[10px] tabular-nums text-muted-foreground">
                        {task.taskNo ?? '—'}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-xs" title={name}>{name}</span>
                      {task.commentCount > 0 && (
                        <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums">
                          <MessageSquare className="h-3 w-3" aria-hidden />
                          {task.commentCount}
                        </span>
                      )}
                    </div>
                    {/* Said in words as well as drawn. A row with no filled bar is otherwise
                        indistinguishable from a row that failed to render. */}
                    {state === 'notStarted' && (
                      <Pill tone="muted">{t('postRecovery.pvaNotStarted', 'not started')}</Pill>
                    )}
                    {state === 'unmeasured' && (
                      <Pill tone="muted">{t('postRecovery.pvaUnmeasured', 'duration not measured')}</Pill>
                    )}
                    {state === 'measured' && task.isOutlier && (
                      <Pill tone="violet">{t('postRecovery.pvaOutlierPill', 'abandoned timer')}</Pill>
                    )}
                  </div>

                  <div className="relative flex-1">
                    {ticks.map((m) => (
                      <div key={m} className="absolute inset-y-0 border-l border-border/40" style={{ left: `${pct(m)}%` }} aria-hidden />
                    ))}

                    {g.plan && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="absolute rounded-sm border border-dashed border-slate-400/80 bg-slate-200/50 dark:border-slate-500/80 dark:bg-slate-700/40"
                            style={{ top: PLAN_TOP, height: PLAN_H, left: `${g.plan.left}%`, width: `${g.plan.width}%`, minWidth: 3 }}
                          />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px] text-xs">
                          <p className="font-medium">{t('postRecovery.pvaTipPlanTitle', 'Plan')}</p>
                          <p>{t('postRecovery.pvaTipPlanBody', '{{d}}, starting {{at}} into the run.', {
                            d: fmt(task.plannedMinutes ?? 0), at: fmt(task.plannedStartMin ?? 0),
                          })}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {g.waiting && g.waiting.width > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="absolute rounded-l-sm border border-slate-400/50"
                            style={{ ...HATCH, top: ACTUAL_TOP, height: ACTUAL_H, left: `${g.waiting.left}%`, width: `${g.waiting.width}%`, minWidth: 2 }}
                          />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px] text-xs">
                          <p className="font-medium">{t('postRecovery.pvaTipWaitTitle', 'Waiting')}</p>
                          <p>{t('postRecovery.pvaTipWaitBody', '{{d}} between the dependencies clearing and work starting. Not the task overrunning — the task not being picked up.', {
                            d: fmt(task.waitingMinutes ?? 0),
                          })}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {g.marker != null && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="absolute w-0.5 rounded-full bg-foreground/70"
                            style={{ top: ACTUAL_TOP - 3, height: ACTUAL_H + 6, left: `${g.marker}%` }}
                          />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[300px] text-xs">
                          <p className="font-medium">{t('postRecovery.pvaTipUnmeasuredTitle', 'Duration not measured')}</p>
                          <p>{t('postRecovery.pvaTipUnmeasuredBody', 'Recorded {{at}} into the run. The task was moved straight to Completed, so no elapsed time was ever observed — a tick, not a bar, because there is no length to draw.', {
                            at: fmt(task.actualStartMin ?? 0),
                          })}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {g.actual && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              'absolute border',
                              g.clipped ? 'rounded-l-sm' : 'rounded-sm',
                              ACTUAL_TONE[tone],
                            )}
                            style={{ top: ACTUAL_TOP, height: ACTUAL_H, left: `${g.actual.left}%`, width: `${g.actual.width}%`, minWidth: 3 }}
                          />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[300px] text-xs">
                          <p className="font-medium">
                            {task.isOutlier
                              ? t('postRecovery.pvaTipOutlierTitle', 'Abandoned timer')
                              : t('postRecovery.pvaTipActualTitle', 'Actual')}
                          </p>
                          <p>{t('postRecovery.pvaTipActualBody', '{{actual}} against a plan of {{planned}}.', {
                            actual: fmt(task.actualMinutes ?? 0),
                            planned: task.plannedMinutes != null ? fmt(task.plannedMinutes) : t('postRecovery.pvaNoPlanned', 'none recorded'),
                          })}</p>
                          {task.isOutlier && (
                            <p className="mt-1 text-muted-foreground">
                              {t('postRecovery.pvaTipOutlierBody', 'Eight hours or more, and over ten times the plan — a timer left running rather than work that took this long. Drawn cut off at the axis and left out of the figures above, but not removed.')}
                            </p>
                          )}
                          {!task.isOutlier && task.varianceMinutes != null && task.varianceMinutes !== 0 && (
                            <p className="mt-1 text-muted-foreground">
                              {task.varianceMinutes > 0
                                ? t('postRecovery.pvaTipOver', '{{d}} over plan.', { d: fmt(task.varianceMinutes) })
                                : t('postRecovery.pvaTipUnder', '{{d}} under plan.', { d: fmt(-task.varianceMinutes) })}
                            </p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {/* The bar runs off the end of the axis. Without this it simply looks like a task
                        that filled the chart, which is a different and much more flattering claim. */}
                    {g.clipped && (
                      <span
                        className="pointer-events-none absolute right-0 z-[1] flex items-center text-violet-700 dark:text-violet-300"
                        style={{ top: ACTUAL_TOP, height: ACTUAL_H }}
                        aria-label={t('postRecovery.pvaContinues', 'continues past the end of the axis')}
                      >
                        <ChevronsRight className="h-3.5 w-3.5" />
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground">
          {t('postRecovery.pvaAxisNote', 'Both bars are minutes from one origin, resolved by the server — a bar further right started later, it did not run longer. The axis is scaled to the tasks that finished within a plausible time; abandoned timers are excluded from the scale so that one seven-week row cannot flatten every other bar on the chart.')}
          {query.data?.originIso ? ' ' + t('postRecovery.pvaOriginAt', 'T+0 is {{at}}.', {
            at: formatDateTime(query.data.originIso),
          }) : ''}
        </p>
      </div>
    </TooltipProvider>
  )
}
